<script setup>
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import authV2LoginIllustrationBorderedDark from '@images/pages/auth-v2-login-illustration-bordered-dark.png'
import authV2LoginIllustrationBorderedLight from '@images/pages/auth-v2-login-illustration-bordered-light.png'
import authV2LoginIllustrationDark from '@images/pages/auth-v2-login-illustration-dark.png'
import authV2LoginIllustrationLight from '@images/pages/auth-v2-login-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { useAuthStore } from '@core/stores/AuthStore'
import { useRouter } from 'vue-router'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { useStrainStore } from '@core/stores/StrainStore'
import { useEquipmentStore } from '@core/stores/EquipmentStore'
import { useSupplyStore } from '@core/stores/SupplyStore'
import { useToolStore } from '@core/stores/ToolStore'
import { useEnvironmentStore } from '@core/stores/EnvironmentStore'
import { useGrowCycleStore } from '@core/stores/GrowCycleStore'
import permanentlyStoreSelectedItems from '@core/permanentlyStoreSelectedItems'

const router = useRouter()

definePage({
  name: 'login',
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const elForm = ref()

const form = ref({
  email: '',
  emailRules: [
    v => !!v || 'The email address is required!',
    value => {
      // eslint-disable-next-line regexp/no-unused-capturing-group
      const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-z\-\d]+\.)+[a-z]{2,}))$/i

      return pattern.test(value) || 'Invalid e-mail.'
    },
  ],

  password: '',
  passwordRules: [
    v => !!v || 'The password is required!',
  ],

  remember: false,
  errorMessage: '',
})

const isPasswordVisible = ref(false)
const authThemeImg = useGenerateImageVariant(authV2LoginIllustrationLight, authV2LoginIllustrationDark, authV2LoginIllustrationBorderedLight, authV2LoginIllustrationBorderedDark, true)
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)
const strainStore = useStrainStore()
const equipmentStore = useEquipmentStore()
const supplyStore = useSupplyStore()
const toolStore = useToolStore()
const environmentStore = useEnvironmentStore()
const growCycleStore = useGrowCycleStore()
const { t } = useI18n()

const { recaptchaLoaded, executeRecaptcha } = useReCaptcha()

const executeLoginRecaptcha = async () => {
  await recaptchaLoaded()

  return executeRecaptcha('login')
}

async function synchronizeStoredEntities() {
  await permanentlyStoreSelectedItems()
  strainStore.resetSelected()
  equipmentStore.resetSelected()
  supplyStore.resetSelected()
  toolStore.resetSelected()
  environmentStore.resetEnvironments()
  growCycleStore.resetGrowCycles()
}

const submitForm = async () => {
  form.value.errorMessage = ''

  const { valid: isFormValid } = await elForm.value.validate()
  if (!isFormValid) {
    return
  }

  const authStore = useAuthStore()

  try {
    const reCaptchaToken = await executeLoginRecaptcha()

    const response = await authStore.authorize({
      email: form.value.email,
      password: form.value.password,
      recaptchaToken: reCaptchaToken,
    })

    await synchronizeStoredEntities()
    router.push('/')
  } catch (e) {
    if (e?.response?.data?.error) {
      if (e.response.data.error.code === 2006) {
        // Handle unsuccessful login attempt
        form.value.errorMessage = 'User & password combination does not exist.'
      } else {
        form.value.errorMessage = e.response.data.error.message
      }
    } else {
      form.value.errorMessage  = t('Something went wrong')
    }
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="d-flex align-center justify-center w-100 h-100"
          style="padding-inline: 6.25rem;"
        >
          <VImg
            max-width="613"
            :src="authThemeImg"
            class="auth-illustration mt-16 mb-2"
          />
        </div>

        <img
          class="auth-footer-mask"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <VForm
            ref="elForm"
            @submit.prevent="submitForm"
          >
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  :rules="form.emailRules"
                  autofocus
                  label="Email"
                  type="email"
                  placeholder="johndoe@email.com"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  :rules="form.passwordRules"
                  :label="$t('Password')"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <div class="d-flex align-center flex-wrap justify-space-between mt-2 mb-4">
                  <VCheckbox
                    v-model="form.remember"
                    :label="$t('Remember me')"
                  />
                  <RouterLink
                    class="text-primary ms-2 mb-1"
                    :to="{ name: 'forgot-password' }"
                  >
                    {{ $t('Forgot Password?') }}
                  </RouterLink>
                </div>

                <ErrorMessage :message="form.errorMessage" />
              </VCol>

              <VCol cols="12">
                <VBtn
                  block
                  type="submit"
                >
                  {{ $t('Login') }}
                </VBtn>
              </VCol>

              <!-- create account -->
              <VCol
                cols="12"
                class="text-center"
              >
                <span>{{ $t('New on our platform?') }}</span>
                <RouterLink
                  class="text-primary ms-2"
                  to="/register"
                >
                  {{ $t('Create an account') }}
                </RouterLink>
              </VCol>
              <!--
                <VCol
                cols="12"
                class="d-flex align-center"
                >
                <VDivider />
                <span class="mx-4">or</span>
                <VDivider />
                </VCol>
              -->
              <!--
                <VCol
                cols="12"
                class="text-center"
                >
                <AuthProvider />
                </VCol>
              -->
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
